import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, User, Building2, ClipboardList, BarChart3, Phone, PhoneOff, Car, DollarSign, Calendar, Wrench } from "lucide-react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import AdminLayout from "@/components/admin/AdminLayout";
import UserFunnelTimeline from "@/components/admin/UserFunnelTimeline";
import type { AdminUser } from "@/components/admin/AdminUserCard";

interface AdminUserDetail extends AdminUser {
  vehicles: any[];
  expenses: any[];
  trips: any[];
  maintenance: any[];
}

const tierColor: Record<string, string> = {
  free: "bg-muted text-muted-foreground",
  pro: "bg-primary/15 text-primary",
  scale: "bg-accent text-accent-foreground",
};

const fmt = (d: string | null) => (d ? format(new Date(d), "MMM d, yyyy") : "—");
const fmtCurrency = (n: number | null) => (n != null ? `$${Number(n).toFixed(2)}` : "—");

export default function AdminUserDetail() {
  const { userId } = useParams<{ userId: string }>();

  const { data: userData, isLoading, error } = useQuery<AdminUserDetail>({
    queryKey: ["admin-user-detail", userId],
    queryFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke("admin-user-detail", {
        body: { user_id: userId },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  const u = userData;

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Link>

        {isLoading && (
          <div className="space-y-6">
            <Skeleton className="h-64 rounded-lg" />
            <Skeleton className="h-96 rounded-lg" />
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            Failed to load user details.
          </div>
        )}

        {u && (
          <div className="space-y-8">
            {/* User Summary Card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="h-5 w-5 shrink-0 text-muted-foreground" />
                      <h1 className="text-xl font-bold truncate">{u.full_name || "—"}</h1>
                      <Badge variant="secondary" className={tierColor[u.subscription_tier] || tierColor.free}>
                        {u.subscription_tier || "free"}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{u.email || "—"}</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mb-4">
                  <span className="flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    {u.company_name || "No company"}
                  </span>
                  <span>·</span>
                  <span>{u.company_size} member{u.company_size !== 1 ? "s" : ""}</span>
                  {u.created_at && (
                    <>
                      <span>·</span>
                      <span>Signed up {fmt(u.created_at)}</span>
                    </>
                  )}
                  {u.last_sign_in_at && (
                    <>
                      <span>·</span>
                      <span>Last sign-in {fmt(u.last_sign_in_at)}</span>
                    </>
                  )}
                </div>

                <div className="mb-4">
                  <UserFunnelTimeline user={u} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Survey */}
                  <div>
                    <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                      <ClipboardList className="h-3.5 w-3.5" />
                      Onboarding Survey
                    </h3>
                    {u.survey ? (
                      <div className="space-y-1 text-sm">
                        <p><span className="text-muted-foreground">Cars:</span> {u.survey.car_count}</p>
                        <p><span className="text-muted-foreground">Challenge:</span> {u.survey.biggest_headache}</p>
                        <p className="flex items-center gap-1">
                          {u.survey.open_to_call ? (
                            <><Phone className="h-3.5 w-3.5 text-primary" /> Open to call</>
                          ) : (
                            <><PhoneOff className="h-3.5 w-3.5 text-muted-foreground" /> Not open to call</>
                          )}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm italic text-muted-foreground">No survey response</p>
                    )}
                  </div>

                  {/* Usage Summary */}
                  <div>
                    <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                      <BarChart3 className="h-3.5 w-3.5" />
                      Product Usage
                    </h3>
                    <div className="grid grid-cols-5 gap-2 text-center">
                      {[
                        { label: "Vehicles", value: u.usage.vehicles },
                        { label: "Expenses", value: u.usage.expenses },
                        { label: "Earnings", value: u.usage.trips },
                        { label: "Maintenance", value: u.usage.maintenance },
                        { label: "AI Chats", value: u.usage.ai_chats },
                      ].map((m) => (
                        <div key={m.label}>
                          <p className="text-lg font-semibold">{m.value}</p>
                          <p className="text-xs text-muted-foreground">{m.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vehicles */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Car className="h-5 w-5" />
                  Vehicles ({u.vehicles.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {u.vehicles.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No vehicles</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Year / Make / Model</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Mileage</TableHead>
                          <TableHead>VIN</TableHead>
                          <TableHead>Added</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {u.vehicles.map((v: any) => (
                          <TableRow key={v.id}>
                            <TableCell className="font-medium">{v.name}</TableCell>
                            <TableCell>{v.year} {v.make} {v.model}{v.trim ? ` ${v.trim}` : ""}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">{v.status || "active"}</Badge>
                            </TableCell>
                            <TableCell>{v.mileage?.toLocaleString() || "—"}</TableCell>
                            <TableCell className="font-mono text-xs">{v.vin || "—"}</TableCell>
                            <TableCell>{fmt(v.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Earnings (Trips) */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <DollarSign className="h-5 w-5" />
                  Earnings ({u.trips.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {u.trips.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No earnings data</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Vehicle</TableHead>
                          <TableHead>Guest</TableHead>
                          <TableHead>Dates</TableHead>
                          <TableHead>Days</TableHead>
                          <TableHead>Revenue</TableHead>
                          <TableHead>Turo Fees</TableHead>
                          <TableHead>Net</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {u.trips.map((t: any) => (
                          <TableRow key={t.id}>
                            <TableCell className="font-medium max-w-[200px] truncate">{t.turo_vehicle_name || "—"}</TableCell>
                            <TableCell>{t.guest_name || "—"}</TableCell>
                            <TableCell className="whitespace-nowrap text-xs">
                              {fmt(t.start_date)} → {fmt(t.end_date)}
                            </TableCell>
                            <TableCell>{t.trip_days ?? "—"}</TableCell>
                            <TableCell>{fmtCurrency(t.revenue)}</TableCell>
                            <TableCell>{fmtCurrency(t.turo_fees)}</TableCell>
                            <TableCell className="font-medium">{fmtCurrency(t.net_revenue ?? t.total_earnings)}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize text-xs">{t.trip_status || "—"}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Expenses */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Calendar className="h-5 w-5" />
                  Expenses ({u.expenses.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {u.expenses.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No expenses</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Paid By</TableHead>
                          <TableHead>Notes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {u.expenses.map((e: any) => (
                          <TableRow key={e.id}>
                            <TableCell className="font-medium">{e.expense_name || e.description || "—"}</TableCell>
                            <TableCell>{e.expense_type || "—"}</TableCell>
                            <TableCell>{fmtCurrency(e.amount)}</TableCell>
                            <TableCell>{fmt(e.expense_date)}</TableCell>
                            <TableCell>{e.paid_by || "—"}</TableCell>
                            <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">{e.notes || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Maintenance */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Wrench className="h-5 w-5" />
                  Maintenance ({u.maintenance.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {u.maintenance.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No maintenance tasks</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Task</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead>Due Mileage</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Completed</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {u.maintenance.map((m: any) => (
                          <TableRow key={m.id}>
                            <TableCell className="font-medium">{m.task_name}</TableCell>
                            <TableCell className="max-w-[250px] truncate text-xs text-muted-foreground">{m.description || "—"}</TableCell>
                            <TableCell>{fmt(m.due_date)}</TableCell>
                            <TableCell>{m.due_at_mileage?.toLocaleString() || "—"}</TableCell>
                            <TableCell>
                              <Badge variant={m.is_completed ? "default" : "outline"}>
                                {m.is_completed ? "Done" : "Pending"}
                              </Badge>
                            </TableCell>
                            <TableCell>{fmt(m.completed_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
